import {
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  StopOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Space, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UnresolvedVendorErrorCodeResponseDto } from '@evimo/client/models';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import {
  CreatedAtSortHeader,
  type CreatedAtSortOrder,
} from '~/app/components/errorManagement/CreatedAtSortHeader/CreatedAtSortHeader';
import {
  ACTION_ICON_COLORS,
  ACTIONS_CLUSTER_WIDTH,
  renderCenteredActions,
  renderDescriptionCell,
  renderErrorCodeCell,
  renderInfoCell,
  renderOccurrenceCountCell,
  withPointerCursor,
} from '~/app/components/errorManagement/tableCellRenderers';
import {
  isUnresolvedEditable,
  isUnresolvedIgnored,
  statusTagColor,
} from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/utils';

/**
 * Pin compact columns. Leave Info + Description without `width` so Ant Design
 * distributes leftover space there (not into Manufacturer).
 */
const MANUFACTURER_COLUMN_WIDTH = 140;
const ERROR_CODE_COLUMN_WIDTH = 140;
const STATUS_COLUMN_WIDTH = 110;
const OCCURRENCES_COLUMN_WIDTH = 120;
const ACTIONS_COLUMN_WIDTH = ACTIONS_CLUSTER_WIDTH + 24;

type UseUnresolvedVendorErrorCodeColumnsArgs = {
  sortOrder: CreatedAtSortOrder;
  onToggleSortOrder: () => void;
  onView: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onEdit: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onResolve: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onIgnore: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onUnignore: (record: UnresolvedVendorErrorCodeResponseDto) => void;
};

export function useUnresolvedVendorErrorCodeColumns({
  sortOrder,
  onToggleSortOrder,
  onView,
  onEdit,
  onResolve,
  onIgnore,
  onUnignore,
}: UseUnresolvedVendorErrorCodeColumnsArgs): ColumnsType<UnresolvedVendorErrorCodeResponseDto> {
  const { t } = useTranslation('errorManagement');

  return withPointerCursor<UnresolvedVendorErrorCodeResponseDto>([
    {
      title: t('columns.manufacturer'),
      dataIndex: ['manufacturer', 'name'],
      key: 'manufacturer',
      width: MANUFACTURER_COLUMN_WIDTH,
      ellipsis: true,
      render: (_value, record) => record.manufacturer?.name ?? '—',
    },
    {
      title: (
        <CreatedAtSortHeader
          label={t('columns.errorCode')}
          sortOrder={sortOrder}
          onToggle={onToggleSortOrder}
        />
      ),
      dataIndex: 'errorCode',
      key: 'errorCode',
      width: ERROR_CODE_COLUMN_WIDTH,
      onHeaderCell: () => ({ style: { overflow: 'visible' } }),
      render: (value: string) => renderErrorCodeCell(value),
    },
    {
      title: t('columns.info'),
      dataIndex: 'info',
      key: 'info',
      ellipsis: true,
      render: (value: string | null | undefined) =>
        renderInfoCell(value, 'fill'),
    },
    {
      title: t('columns.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (value: string | null | undefined) =>
        renderDescriptionCell(value, 'fill'),
    },
    {
      title: t('columns.sourceDescription'),
      dataIndex: 'sourceDescription',
      key: 'sourceDescription',
      ellipsis: true,
      render: (value: string | null | undefined) =>
        renderDescriptionCell(value, 'fill'),
    },
    {
      title: t('unresolved.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: STATUS_COLUMN_WIDTH,
      render: (value: string) => (
        <Tag color={statusTagColor(value)} style={{ marginInlineEnd: 0 }}>
          {t(`unresolved.status.${value}`)}
        </Tag>
      ),
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('unresolved.columns.occurrenceCount')}
        </span>
      ),
      dataIndex: 'occurrenceCount',
      key: 'occurrenceCount',
      width: OCCURRENCES_COLUMN_WIDTH,
      align: 'center',
      render: (value: number) => renderOccurrenceCountCell(value),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      width: ACTIONS_COLUMN_WIDTH,
      align: 'center',
      render: (_value, record) => {
        const canEdit = isUnresolvedEditable(record.status);
        const canUnignore = isUnresolvedIgnored(record.status);

        return renderCenteredActions(
          <Space size={2} wrap={false}>
            <Tooltip title={t('actions.view')}>
              <Button
                type="text"
                size="small"
                aria-label={t('actions.view')}
                icon={
                  <EyeOutlined style={{ color: ACTION_ICON_COLORS.view }} />
                }
                onClick={() => onView(record)}
              />
            </Tooltip>
            {canEdit ? (
              <>
                <Tooltip title={t('actions.edit')}>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t('actions.edit')}
                    icon={
                      <EditOutlined
                        style={{ color: ACTION_ICON_COLORS.edit }}
                      />
                    }
                    onClick={() => onEdit(record)}
                  />
                </Tooltip>
                <Tooltip title={t('unresolved.actions.resolve')}>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t('unresolved.actions.resolve')}
                    icon={
                      <CheckCircleOutlined
                        style={{ color: ACTION_ICON_COLORS.resolve }}
                      />
                    }
                    onClick={() => onResolve(record)}
                  />
                </Tooltip>
                <Tooltip title={t('unresolved.actions.ignore')}>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t('unresolved.actions.ignore')}
                    icon={
                      <StopOutlined
                        style={{ color: ACTION_ICON_COLORS.ignore }}
                      />
                    }
                    onClick={() => onIgnore(record)}
                  />
                </Tooltip>
              </>
            ) : null}
            {canUnignore ? (
              <Tooltip title={t('unresolved.actions.unignore')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={t('unresolved.actions.unignore')}
                  icon={
                    <UndoOutlined
                      style={{ color: ACTION_ICON_COLORS.unignore }}
                    />
                  }
                  onClick={() => onUnignore(record)}
                />
              </Tooltip>
            ) : null}
          </Space>,
        );
      },
    },
  ]);
}
